/**
 * Grid Density Controls - Handles grid layout changes based on user selection
 * Listens for clicks on grid density option spans and updates product-list classes
 * Persists selection across AJAX updates using localStorage
 */

import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

const storageKey = 'grid-density-selection';
const domUpdateDelay = 100; // Delay in ms for DOM updates to complete

// AbortController for managing event listeners
let abortController = new AbortController();

/**
 * Saves the selected grid density value to localStorage
 * @param {string} value - The selected value ('default' or 'mobile-single')
 */
function saveGridDensitySelection(value) {
  try {
    localStorage.setItem(storageKey, value);
  } catch (e) {
    // localStorage may not be available in some contexts
    console.warn('Could not save grid density selection:', e);
  }
}

/**
 * Gets the saved grid density selection from localStorage
 * @returns {string|null} The saved value or null if not found
 */
function getSavedGridDensitySelection() {
  try {
    return localStorage.getItem(storageKey);
  } catch (e) {
    return null;
  }
}

/**
 * Applies grid density classes to product-list elements
 * @param {string} selectedValue - The selected value ('default' or 'mobile-single')
 */
function applyGridDensityToProductList(selectedValue) {
  const productListElements = document.querySelectorAll('product-list.product-list, .product-list');

  productListElements.forEach((productList) => {
    // Remove existing classes
    productList.classList.remove('grid-density-single', 'grid-density-default');
    
    // Add class based on value
    if (selectedValue === 'mobile-single') {
      productList.classList.add('grid-density-single');
    } else {
      productList.classList.add('grid-density-default');
    }
  });
}

/**
 * Handles grid density change event
 * @param {HTMLElement} optionElement - The clicked option element
 * @param {boolean} skipSave - Whether to skip saving to localStorage (for restoration)
 */
function handleGridDensityChange(optionElement, skipSave = false) {
  const selectedValue = optionElement.dataset.gridValue;
  if (!selectedValue) return;

  const allOptions = document.querySelectorAll('.column-options__option[data-grid-value]');

  // Update active state
  allOptions.forEach((option) => {
    option.classList.toggle('is-active', option === optionElement);
  });

  // Save to localStorage (unless we're restoring from saved value)
  if (!skipSave) {
    saveGridDensitySelection(selectedValue);
  }

  // Update product-list classes
  applyGridDensityToProductList(selectedValue);
}

/**
 * Handles click event on grid density option
 * @param {Event} event - The click event
 */
function handleOptionClick(event) {
  const optionElement = event.currentTarget;
  if (optionElement.dataset.gridValue) {
    handleGridDensityChange(optionElement);
  }
}

/**
 * Handles keyboard event (Enter/Space) on grid density option
 * @param {Event} event - The keyboard event
 */
function handleOptionKeydown(event) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleOptionClick(event);
  }
}

/**
 * Restores the saved grid density selection or applies default
 */
function restoreOrApplyDefault() {
  const gridOptions = document.querySelectorAll('.column-options__option[data-grid-value]');
  if (gridOptions.length === 0) return;

  const savedValue = getSavedGridDensitySelection();
  const savedOption = savedValue 
    ? Array.from(gridOptions).find(opt => opt.dataset.gridValue === savedValue)
    : null;

  if (savedOption) {
    // Restore saved selection
    handleGridDensityChange(savedOption, true);
    return;
  }

  // Check for existing active option
  const activeOption = Array.from(gridOptions).find(opt => opt.classList.contains('is-active'));
  if (activeOption) {
    // Ensure product-list classes match the active option
    const activeValue = activeOption.dataset.gridValue;
    if (activeValue) {
      applyGridDensityToProductList(activeValue);
    }
    return;
  }

  // Default to 'default' view if no saved or active option
  const defaultOption = Array.from(gridOptions).find(opt => opt.dataset.gridValue === 'default');
  if (defaultOption) {
    handleGridDensityChange(defaultOption);
  }
}

/**
 * Initializes grid density controls
 */
function initGridDensityControls() {
  // Abort previous listeners
  abortController.abort();
  abortController = new AbortController();
  const { signal } = abortController;

  const gridOptions = document.querySelectorAll('.column-options__option[data-grid-value]');
  if (gridOptions.length === 0) {
    return;
  }

  // Add event listeners with abort signal for cleanup
  gridOptions.forEach((option) => {
    option.addEventListener('click', handleOptionClick, { signal });
    option.addEventListener('keydown', handleOptionKeydown, { signal });
  });

  // Restore saved selection or apply default
  restoreOrApplyDefault();
}

/**
 * Sets up MutationObserver to watch for product list updates
 */
function setupProductGridObserver() {
  const productGridContainer = document.querySelector('#ProductGridContainer');
  if (!productGridContainer || productGridContainer._gridDensityObserver) {
    return;
  }

  const observer = new MutationObserver(() => {
    // Debounce rapid mutations
    clearTimeout(observer._timeout);
    observer._timeout = setTimeout(() => {
      initGridDensityControls();
    }, domUpdateDelay);
  });

  observer.observe(productGridContainer, {
    childList: true,
    subtree: true
  });

  productGridContainer._gridDensityObserver = observer;
}

/**
 * Handles AJAX updates by re-initializing controls
 */
function handleAjaxUpdate() {
  setTimeout(() => {
    initGridDensityControls();
  }, domUpdateDelay);
}

/**
 * Main initialization function
 */
function init() {
  initGridDensityControls();
  setupProductGridObserver();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-initialize when new content is loaded (for AJAX updates)
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:block:select', init);

// Listen for ThemeBus events (used by this theme's filter system)
ThemeBus.addEventListener(EVENTS.filtersUpdated, handleAjaxUpdate);
ThemeBus.addEventListener(EVENTS.gridUpdated, handleAjaxUpdate);
ThemeBus.addEventListener(EVENTS.filtersApplied, handleAjaxUpdate);

// Fallback: Listen for document-level events
document.addEventListener('filtersUpdated', handleAjaxUpdate);
document.addEventListener('gridUpdated', handleAjaxUpdate);
document.addEventListener('collection:filtered', handleAjaxUpdate);
document.addEventListener('collection:sorted', handleAjaxUpdate);
document.addEventListener('collection:paginated', handleAjaxUpdate);
document.addEventListener('facet:updated', handleAjaxUpdate);
document.addEventListener('collection:updated', handleAjaxUpdate);
document.addEventListener('productList:updated', handleAjaxUpdate);

// Listen for popstate (browser back/forward and AJAX navigation)
window.addEventListener('popstate', handleAjaxUpdate);

